import { FocusKeyManager } from '@angular/cdk/a11y';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, PLATFORM_ID, ElementRef, EventEmitter, Component, Input, Output, ViewChild, ChangeDetectorRef, NgZone, HostBinding, HostListener, NgModule } from '@angular/core';
import { correctKeyRTL, responsivelyCheckDirection } from '@simpl/element-ng/common';
import { SiListInteractionService } from '@simpl/element-ng/list-interaction';
import * as i2 from '@simpl/element-ng/link';
import { SiLinkDirective, SiLinkModule } from '@simpl/element-ng/link';
import * as i3 from '@simpl/element-ng/translate';
import { SiTranslateModule } from '@simpl/element-ng/translate';

class SiMenuItemComponent {
    interactableListItem;
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    _elementRef = inject((ElementRef));
    listInteractionService = inject(SiListInteractionService);
    /**
     * Menu item to display.
     */
    item;
    /**
     * Specifies the badge style.
     */
    badgeStyle = '';
    _dropdownCaret = '';
    /**
     * Specifies the dropdown caret direction.
     * @defaultref {@link _dropdownCaret}
     */
    set dropdownCaret(caret) {
        this._dropdownCaret = caret;
    }
    get dropdownCaret() {
        return this._dropdownCaret;
    }
    /**
     * Specifies an optional parameter that will be passed to the action function
     * of the item if it implements it.
     */
    actionParam;
    /**
     * If item has children, do not allow it to be clicked.
     */
    disableParentClick = false;
    /**
     * If item does not have a link, action or children, do not allow it to be clicked.
     */
    disableLinklessClick = false;
    /**
     * Whether to disable the list interactability, used when only using this
     * for the styling or `MenuItem` interface. Also enables this to be manually focused.
     */
    disableListInteraction = false;
    /**
     * Specifies whether the menu item focus styling should be inside instead of outside.
     */
    focusStylingInside = false;
    /**
     * Forces the active styling
     */
    forceActive = false;
    /**
     * Force-show title, when iconOnly is set
     */
    forceTitle = false;
    /**
     * Class to use on the link
     */
    itemClass = 'dropdown-item';
    /**
     * An optional siDropdownToggle instance to automatically open and close the dropdown on list interaction navigation
     */
    dropdownToggle;
    get disabled() {
        return (!(this._elementRef.nativeElement.offsetParent ||
            (this.isBrowser &&
                window.getComputedStyle(this._elementRef.nativeElement).position === 'fixed' &&
                window.getComputedStyle(this._elementRef.nativeElement).display !== 'none')) ||
            this.item.disabled ||
            this.item.title === '-' ||
            this.item.isHeading ||
            ((!this.item.items || this.item.items.length === 0) &&
                !this.item.action &&
                !this.item.link &&
                !this.item.href &&
                this.disableLinklessClick));
    }
    get theBadgeStyle() {
        return this.item.badgeStyle ?? this.badgeStyle;
    }
    get iconOnly() {
        return !!this.item.iconOnly && !this.forceTitle;
    }
    activeChange = new EventEmitter();
    // The badge character limit is restricted to three characters.
    // In case when the badge is of type `string` and the number of characters are more
    // than three, then only the first three characters will be displayed
    get badgeText() {
        const badge = this.item.badge;
        return typeof badge === 'string' && badge.length > 3
            ? badge.slice(0, 3)
            : typeof badge === 'number' && badge > 99
                ? '99+'
                : badge;
    }
    menuItem;
    link;
    linkActiveChange(active) {
        this.active = active;
        this.activeChange.emit(active);
    }
    active = false;
    get parentList() {
        if (this.interactableListItem?.parent) {
            return this.interactableListItem.parent.directive;
        }
        return null;
    }
    get childList() {
        if (this.interactableListItem?.child &&
            this.interactableListItem.child.trigger?.element === this.interactableListItem.element) {
            return this.interactableListItem.child.directive;
        }
        return null;
    }
    get iconClass() {
        let iconClass = this.item.icon;
        // Use -filled icon if item is active
        // Keep the default icon in case that icon does not have a filled version
        if (this.item.isActive ||
            this.link?.active ||
            this.getHostElement().classList.contains('show')) {
            iconClass = iconClass + ' ' + iconClass + '-filled';
        }
        return iconClass;
    }
    ngOnInit() {
        if (!this.disableListInteraction) {
            this.interactableListItem = this.listInteractionService.addInteractableListItem(this, this._elementRef.nativeElement);
        }
    }
    ngOnDestroy() {
        if (this.interactableListItem) {
            this.listInteractionService.removeInteractableListItem(this.interactableListItem);
        }
    }
    /** Focuses the menu item. */
    focus(origin, options) {
        if (this.interactableListItem?.parent) {
            this.interactableListItem.parent.directive.setActiveItem(this);
        }
        this.getItemElement().focus(options);
    }
    /** Returns the item DOM element. */
    getItemElement() {
        return this.menuItem.nativeElement;
    }
    /** Returns the parent DOM element. */
    getParentElement() {
        return this._elementRef.nativeElement.parentElement;
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
    hasChildElement(element) {
        return this.interactableListItem
            ? this.listInteractionService.isChildElementOfItem(this.interactableListItem, element)
            : this._elementRef.nativeElement.contains(element);
    }
    getItemRole(item) {
        if (item.selectionState === 'check' || item.type === 'check') {
            return 'menuitemcheckbox';
        }
        if (item.selectionState === 'radio' || item.type === 'radio') {
            return 'menuitemradio';
        }
        return 'menuitem';
    }
    getAriaChecked(item) {
        if (item.selectionState === 'check' || item.selectionState === 'radio') {
            return true;
        }
        else if (item.type === 'check' || item.type === 'radio') {
            return false;
        }
        return null;
    }
    handleSpace() {
        if (this.item.type) {
            this.menuItem.nativeElement.click();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiMenuItemComponent, selector: "si-menu-item", inputs: { item: "item", badgeStyle: "badgeStyle", dropdownCaret: "dropdownCaret", actionParam: "actionParam", disableParentClick: "disableParentClick", disableLinklessClick: "disableLinklessClick", disableListInteraction: "disableListInteraction", focusStylingInside: "focusStylingInside", forceActive: "forceActive", forceTitle: "forceTitle", itemClass: "itemClass", dropdownToggle: "dropdownToggle" }, outputs: { activeChange: "activeChange" }, viewQueries: [{ propertyName: "menuItem", first: true, predicate: ["menuItem"], descendants: true }, { propertyName: "link", first: true, predicate: SiLinkDirective, descendants: true }], ngImport: i0, template: "<a\n  *ngIf=\"\n    (!disableParentClick || !item.items?.length) &&\n    (!disableLinklessClick || item.action || item.link || item.href) &&\n    !item.isHeading\n  \"\n  #menuItem\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-icon-only]=\"\n    iconOnly ||\n    (!item.title &&\n      item.icon &&\n      !item.selectionState &&\n      !dropdownCaret &&\n      (!item.items || item.items.length === 0))\n  \"\n  [class.dropdown-item-no-link]=\"item.type\"\n  [siLink]=\"item\"\n  [actionParam]=\"actionParam\"\n  [attr.tabindex]=\"disableListInteraction ? '' : '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n  (activeChange)=\"linkActiveChange($event)\"\n  (keydown.space)=\"handleSpace()\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n<a\n  *ngIf=\"\n    (disableParentClick && item.items?.length) ||\n    (disableLinklessClick && !item.action && !item.link && !item.href) ||\n    item.isHeading\n  \"\n  #menuItem\n  class=\"dropdown-item-no-link\"\n  [class.dropdown-header]=\"item.isHeading\"\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-static]=\"\n    (!item.items || item.items.length === 0) &&\n    ((disableLinklessClick && !item.action && !item.link && !item.href) || item.isHeading)\n  \"\n  [class.dropdown-item-icon-only]=\"\n    iconOnly ||\n    (!item.title && item.icon && !item.selectionState && !dropdownCaret && !item.items?.length)\n  \"\n  [attr.tabindex]=\"disableListInteraction ? '' : '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n\n<ng-template #itemTemplate>\n  <span\n    *ngIf=\"item.icon\"\n    class=\"item-icon icon\"\n    [ngClass]=\"iconClass!\"\n    [class.badge-dot]=\"item.badgeDot\"\n  ></span>\n  <span\n    *ngIf=\"\n      (theBadgeStyle === 'dot' || (!theBadgeStyle && (iconOnly || (!item.title && item.icon)))) &&\n      item.badge &&\n      item.icon\n    \"\n    class=\"badge-text\"\n    >{{ badgeText }}</span\n  >\n  <span\n    *ngIf=\"\n      !iconOnly &&\n      (item.title || !item.icon || item.selectionState || dropdownCaret || item.items?.length)\n    \"\n    class=\"item-title text-truncate\"\n    >{{ item.title! | translate }}</span\n  >\n  <span\n    *ngIf=\"\n      (theBadgeStyle === 'inline' || (!theBadgeStyle && !iconOnly && (item.title || !item.icon))) &&\n      item.badge\n    \"\n    class=\"badge me-n2\"\n    [ngClass]=\"'bg-' + (item.badgeColor || 'default')\"\n    >{{ item.badge }}</span\n  >\n  <span\n    *ngIf=\"!iconOnly && item.selectionState && !dropdownCaret && !item.items?.length\"\n    class=\"icon menu-end-icon ms-2\"\n    [class.element-ok]=\"item.selectionState === 'check'\"\n    [class.element-record-filled]=\"item.selectionState === 'radio'\"\n  ></span>\n  <span\n    *ngIf=\"!iconOnly && (item.items?.length || dropdownCaret) && dropdownCaret !== 'none'\"\n    class=\"icon menu-end-icon element-down-2 dropdown-caret-animation flip-rtl ms-2\"\n    [class.dropdown-caret-end]=\"dropdownCaret === 'end'\"\n    [class.dropdown-caret-start]=\"dropdownCaret === 'start'\"\n    [class.dropdown-caret-up]=\"dropdownCaret === 'up'\"\n  ></span>\n</ng-template>\n", styles: [".dropdown-caret-animation:before{display:inline-block;transform:rotate(var(--caret-rotate, 0deg));transition:transform calc(.4s * var(--element-animations-enabled, 1))}:host.show .dropdown-caret-animation:before{transform:rotate(calc(var(--caret-rotate, 0deg) + 180deg))}.dropdown-caret-end{--caret-rotate: -90deg}.dropdown-caret-start{--caret-rotate: 90deg}.dropdown-caret-up{--caret-rotate: 180deg}.dropdown-item:not(.dropdown-header):not(.dropdown-item-static):not(.disabled){cursor:pointer}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.SiLinkDirective, selector: "[siLink]", inputs: ["siLink", "siLinkDefaultTarget", "actionParam", "activeClass", "exactMatch", "ariaCurrent"], outputs: ["activeChange"], exportAs: ["siLink"] }, { kind: "pipe", type: i3.SiTranslatePipe, name: "translate" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-menu-item', template: "<a\n  *ngIf=\"\n    (!disableParentClick || !item.items?.length) &&\n    (!disableLinklessClick || item.action || item.link || item.href) &&\n    !item.isHeading\n  \"\n  #menuItem\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-icon-only]=\"\n    iconOnly ||\n    (!item.title &&\n      item.icon &&\n      !item.selectionState &&\n      !dropdownCaret &&\n      (!item.items || item.items.length === 0))\n  \"\n  [class.dropdown-item-no-link]=\"item.type\"\n  [siLink]=\"item\"\n  [actionParam]=\"actionParam\"\n  [attr.tabindex]=\"disableListInteraction ? '' : '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n  (activeChange)=\"linkActiveChange($event)\"\n  (keydown.space)=\"handleSpace()\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n<a\n  *ngIf=\"\n    (disableParentClick && item.items?.length) ||\n    (disableLinklessClick && !item.action && !item.link && !item.href) ||\n    item.isHeading\n  \"\n  #menuItem\n  class=\"dropdown-item-no-link\"\n  [class.dropdown-header]=\"item.isHeading\"\n  [ngClass]=\"itemClass\"\n  [class.focus-inside]=\"focusStylingInside\"\n  [class.active]=\"forceActive || active\"\n  [class.disabled]=\"item.disabled\"\n  [class.dropdown-item-static]=\"\n    (!item.items || item.items.length === 0) &&\n    ((disableLinklessClick && !item.action && !item.link && !item.href) || item.isHeading)\n  \"\n  [class.dropdown-item-icon-only]=\"\n    iconOnly ||\n    (!item.title && item.icon && !item.selectionState && !dropdownCaret && !item.items?.length)\n  \"\n  [attr.tabindex]=\"disableListInteraction ? '' : '-1'\"\n  [attr.aria-label]=\"item.title! | translate\"\n  [attr.aria-disabled]=\"item.disabled\"\n  [attr.aria-checked]=\"getAriaChecked(item)\"\n  [attr.data-id]=\"item.id\"\n  [attr.role]=\"getItemRole(item)\"\n>\n  <ng-container *ngTemplateOutlet=\"itemTemplate\"></ng-container>\n</a>\n\n<ng-template #itemTemplate>\n  <span\n    *ngIf=\"item.icon\"\n    class=\"item-icon icon\"\n    [ngClass]=\"iconClass!\"\n    [class.badge-dot]=\"item.badgeDot\"\n  ></span>\n  <span\n    *ngIf=\"\n      (theBadgeStyle === 'dot' || (!theBadgeStyle && (iconOnly || (!item.title && item.icon)))) &&\n      item.badge &&\n      item.icon\n    \"\n    class=\"badge-text\"\n    >{{ badgeText }}</span\n  >\n  <span\n    *ngIf=\"\n      !iconOnly &&\n      (item.title || !item.icon || item.selectionState || dropdownCaret || item.items?.length)\n    \"\n    class=\"item-title text-truncate\"\n    >{{ item.title! | translate }}</span\n  >\n  <span\n    *ngIf=\"\n      (theBadgeStyle === 'inline' || (!theBadgeStyle && !iconOnly && (item.title || !item.icon))) &&\n      item.badge\n    \"\n    class=\"badge me-n2\"\n    [ngClass]=\"'bg-' + (item.badgeColor || 'default')\"\n    >{{ item.badge }}</span\n  >\n  <span\n    *ngIf=\"!iconOnly && item.selectionState && !dropdownCaret && !item.items?.length\"\n    class=\"icon menu-end-icon ms-2\"\n    [class.element-ok]=\"item.selectionState === 'check'\"\n    [class.element-record-filled]=\"item.selectionState === 'radio'\"\n  ></span>\n  <span\n    *ngIf=\"!iconOnly && (item.items?.length || dropdownCaret) && dropdownCaret !== 'none'\"\n    class=\"icon menu-end-icon element-down-2 dropdown-caret-animation flip-rtl ms-2\"\n    [class.dropdown-caret-end]=\"dropdownCaret === 'end'\"\n    [class.dropdown-caret-start]=\"dropdownCaret === 'start'\"\n    [class.dropdown-caret-up]=\"dropdownCaret === 'up'\"\n  ></span>\n</ng-template>\n", styles: [".dropdown-caret-animation:before{display:inline-block;transform:rotate(var(--caret-rotate, 0deg));transition:transform calc(.4s * var(--element-animations-enabled, 1))}:host.show .dropdown-caret-animation:before{transform:rotate(calc(var(--caret-rotate, 0deg) + 180deg))}.dropdown-caret-end{--caret-rotate: -90deg}.dropdown-caret-start{--caret-rotate: 90deg}.dropdown-caret-up{--caret-rotate: 180deg}.dropdown-item:not(.dropdown-header):not(.dropdown-item-static):not(.disabled){cursor:pointer}\n"] }]
        }], propDecorators: { item: [{
                type: Input,
                args: [{ required: true }]
            }], badgeStyle: [{
                type: Input
            }], dropdownCaret: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], disableParentClick: [{
                type: Input
            }], disableLinklessClick: [{
                type: Input
            }], disableListInteraction: [{
                type: Input
            }], focusStylingInside: [{
                type: Input
            }], forceActive: [{
                type: Input
            }], forceTitle: [{
                type: Input
            }], itemClass: [{
                type: Input
            }], dropdownToggle: [{
                type: Input
            }], activeChange: [{
                type: Output
            }], menuItem: [{
                type: ViewChild,
                args: ['menuItem']
            }], link: [{
                type: ViewChild,
                args: [SiLinkDirective]
            }] } });

/*
 * NOTICE: This component uses the same logic as the `si-list-interaction` directive
 * and is compatible with it.
 */
class SiMenuComponent {
    /**
     * Array of menu items to display.
     */
    items = [];
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link dropstart}
     * @deprecated Use the new Input {@link dropstart}
     */
    set dropleft(value) {
        this.dropstart = value;
    }
    get dropleft() {
        return this.dropstart;
    }
    /**
     * Specifies whether sub menus should be displayed on the start side.
     */
    dropstart = false;
    get start() {
        return this.actualDropstart;
    }
    /**
     * Specifies whether sub menus should be displayed on the start side.
     * @defaultref {@link responsiveDropstart}
     * @deprecated Use the new Input {@link responsiveDropstart}
     */
    set responsiveDropleft(value) {
        this.responsiveDropstart = value;
    }
    get responsiveDropleft() {
        return this.responsiveDropstart;
    }
    /**
     * If `true`, the sub-menus will automatically open on the other side, if there is not enough space on the preferred one.
     */
    responsiveDropstart = true;
    /**
     * If `true`, the sub-menus will automatically open upwards, if there is not enough space below.
     */
    responsivelyDropUpwards = true;
    /**
     * Specifies an optional parameter that will be passed to the action function
     * of items that implement it.
     */
    actionParam;
    /**
     * Specifies whether the component should automatically be focused as soon as it is loaded.
     */
    focusOnLoad = false;
    /**
     * Specifies if any and which item should be focused when the component is focused.
     */
    focus = '';
    _focusLastFromParent = false;
    /**
     * Specifies if the last item should be focused when navigating to this menu from a parent list (instead of the first).
     * @defaultref {@link _focusLastFromParent}
     */
    set focusLastFromParent(focusLastFromParent) {
        this._focusLastFromParent = focusLastFromParent;
        if (this.interactableList) {
            this.interactableList.focusLastFromParent.next(focusLastFromParent);
        }
    }
    get focusLastFromParent() {
        return this._focusLastFromParent;
    }
    /**
     * Specifies whether the menu is a sub list and a parent item should automatically be searched or be defined.
     */
    isSubList = false;
    _parentItem = null;
    /**
     * Specifies a specific item that is the parent of the menu if the automatic one is incorrect.
     * @defaultref {@link _parentItem}
     */
    set parentItem(item) {
        this._parentItem = item;
        if (this.interactableList) {
            this.interactableList.triggerDefined.next(item);
        }
    }
    get parentItem() {
        if (!this._parentItem && this.interactableList?.trigger) {
            return this.interactableList.trigger.directive;
        }
        return this._parentItem;
    }
    /**
     * Automatically refocus after pressing enter on one of the items.
     * Use when losing focus causes issues with keyboard interaction.
     */
    refocusAfterEnter = true;
    orientation = 'vert';
    _position = 'integrated';
    /**
     * Specifies the position of the list relative to the parent.
     * Only relevant if this is a sub list and the parent has a different orientation.
     * @defaultref {@link _position}
     */
    set position(position) {
        this._position = position;
        if (this.interactableList) {
            this.interactableList.position.next(position);
        }
    }
    get position() {
        return this._position;
    }
    autofocusFromParent = false;
    /**
     * Specifies whether the menu should use relative positioning and be open by default.
     * Used when not combined with a dropdown.
     */
    relativePositioning = false;
    /**
     * Force-show title, when iconOnly is set
     */
    forceTitle = false;
    activeChange = new EventEmitter();
    keepOpen = false;
    tabindex = '';
    interactableList;
    linkActiveChange(active) {
        const newActive = active ||
            this.interactableList.items.some(item => item.directive.active) ||
            this.interactableList.nested.some(list => list.directive.active) ||
            false;
        if (newActive !== this.active) {
            this.active = newActive;
            this.activeChange.emit(this.active);
        }
    }
    active = false;
    get parentList() {
        if (this.interactableList?.parent) {
            return this.interactableList.parent.directive;
        }
        return null;
    }
    get parentListAll() {
        if (this.interactableList &&
            (this.interactableList.parent || this.interactableList.parentDisconnected)) {
            return this.interactableList.parent
                ? this.interactableList.parent.directive
                : this.interactableList.parentDisconnected.directive;
        }
        return null;
    }
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    connectionsCalculated = false;
    ignoreNextChange = false;
    _keyManager;
    _previousChild;
    _ignoreKeydown = undefined;
    _changeSubscription;
    _tabOutSubscription;
    childrenHidden = false;
    listenersSet = false;
    observer;
    entries = [];
    _elementRef = inject((ElementRef));
    _changeDetector = inject(ChangeDetectorRef);
    listInteractionService = inject(SiListInteractionService);
    zone = inject(NgZone);
    /**
     * Indicate whether the child menu open to left or right.
     */
    actualDropstart = false;
    trackByIndex = (index) => index;
    ngAfterViewInit() {
        this._keyManager = new FocusKeyManager(this.interactableList.allItemsQueryList).withWrap();
        this._changeSubscription = this._keyManager.change.subscribe(() => {
            if (!this.ignoreNextChange) {
                this.openAndCloseChildren();
                addEventListener('click', this.onClickOut);
            }
            this.ignoreNextChange = false;
        });
        this._tabOutSubscription = this._keyManager.tabOut.subscribe(() => {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        });
    }
    ngOnInit() {
        this.interactableList = this.listInteractionService.addInteractableList(this, this._elementRef.nativeElement, this._parentItem, this._position, 'vert', this.focusLastFromParent, this.isSubList, () => this.calculated());
        this.interactableList.orientation.next('vert');
        const newTabindex = !this.isSubList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
    }
    ngOnDestroy() {
        this._changeSubscription?.unsubscribe();
        this._tabOutSubscription?.unsubscribe();
        if (this.interactableList) {
            this.listInteractionService.removeInteractableList(this.interactableList);
        }
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
            this.zone.runOutsideAngular(() => {
                removeEventListener('scroll', this.onParentScrollOrResize, true);
                removeEventListener('resize', this.onParentScrollOrResize);
            });
        }
    }
    ngOnChanges(changes) {
        if (changes.items ||
            changes.dropstart ||
            changes.responsiveDropstart ||
            changes.responsivelyDropUpwards) {
            this.actualDropstart = this.dropstart;
            this.observer?.disconnect();
            this.observer = undefined;
            this.childrenHidden = false;
            setTimeout(() => {
                this.entries = [];
                if ((!this.responsiveDropstart && !this.responsivelyDropUpwards) ||
                    !this.hasAnyChildren() ||
                    !this.isBrowser) {
                    return;
                }
                const menu = this.getHostElement();
                if (!menu?.firstElementChild) {
                    return;
                }
                const children = Array.from(menu.firstElementChild.children);
                if (!children) {
                    return;
                }
                this.items.forEach((item, index) => {
                    const found = item.items?.length ? children[index]?.querySelector('SI-MENU') : undefined;
                    if (found) {
                        const foundItem = children[index]?.querySelector('SI-MENU-ITEM');
                        if (foundItem) {
                            this.entries.push({
                                element: found,
                                itemElement: foundItem,
                                item,
                                isVisible: false
                            });
                        }
                    }
                });
                if (!this.entries.length) {
                    return;
                }
                this.childrenHidden = true;
                this.observer = new IntersectionObserver(visibilities => {
                    let didChanges = false;
                    visibilities.forEach(visibility => {
                        const entry = this.entries.find(item => item.element === visibility.target);
                        if (entry) {
                            if (visibility.isIntersecting) {
                                if (!entry.isVisible) {
                                    if (entry.itemElement !== this._keyManager.activeItem?.getHostElement()) {
                                        this.closePreviousChild();
                                    }
                                    entry.isVisible = true;
                                    didChanges = true;
                                    if (this.responsivelyDropUpwards) {
                                        this.responsivelyCheckIfUpwards(entry.element, entry.itemElement, entry.item);
                                    }
                                }
                            }
                            else if (entry.isVisible) {
                                entry.isVisible = false;
                                didChanges = true;
                            }
                        }
                    });
                    if (didChanges && (this.responsiveDropstart || this.responsivelyDropUpwards)) {
                        const visibleEntries = this.entries
                            .filter(item => item.isVisible)
                            .map(item => item.element);
                        if (visibleEntries.length) {
                            if (this.responsiveDropstart) {
                                this.responsivelyChangeDirection(visibleEntries);
                            }
                            if (this.childrenHidden) {
                                this.childrenHidden = false;
                            }
                            if (!this.listenersSet && this.isBrowser) {
                                this.zone.runOutsideAngular(() => {
                                    // Add event listener to window as capturing to handle tested element scrolling
                                    addEventListener('scroll', this.onParentScrollOrResize, true);
                                    // here we _actually_ want window.resize, nothing else since this is about a fixed position
                                    addEventListener('resize', this.onParentScrollOrResize);
                                });
                                this.listenersSet = true;
                            }
                        }
                        else if (!this.childrenHidden) {
                            this.childrenHidden = true;
                        }
                    }
                });
                this.entries.forEach(entry => {
                    this.observer.observe(entry.element);
                });
            });
        }
    }
    calculated() {
        this.connectionsCalculated = true;
        const newTabindex = !this.isSubList || !this.parentList ? '0' : '';
        if (this.tabindex !== newTabindex) {
            this.tabindex = newTabindex;
        }
        if (this.isBrowser && document.activeElement === this.getHostElement()) {
            if (this.focus) {
                this.focusFirstItem('program', this.focus === 'last');
            }
        }
        else if (this.focusOnLoad) {
            this._elementRef.nativeElement.focus();
        }
    }
    /** Ignore the next keydown in this and all parent menus. Called from sub menu. */
    ignoreKeydown(event) {
        if (this.parentListAll) {
            this.parentListAll.ignoreKeydown(event);
        }
        this._ignoreKeydown = event;
    }
    /** Handle a keyboard event from the menu, delegating to the appropriate action. */
    handleKeydown(event) {
        if (this._ignoreKeydown?.key === event.key && this._ignoreKeydown.target === event.target) {
            this._ignoreKeydown = undefined;
        }
        else {
            if (this.parentListAll) {
                // If this is a sub menu ignore the keydown in all parent menus (due to event bubbling).
                this.parentListAll.ignoreKeydown(event);
            }
            const key = event.key;
            const manager = this._keyManager;
            const rtlCorrectedKey = correctKeyRTL(key);
            switch (rtlCorrectedKey) {
                case 'Escape':
                    if (!event.altKey && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
                        event.preventDefault();
                        this.goDownLevel();
                    }
                    break;
                case 'ArrowLeft':
                    if (this.actualDropstart) {
                        this.goUpLevel(false, true);
                    }
                    else {
                        this.goDownLevel();
                    }
                    break;
                case 'ArrowRight':
                    if (this.actualDropstart) {
                        this.goDownLevel(false, true);
                    }
                    else {
                        this.goUpLevel();
                    }
                    break;
                case 'Enter':
                    if (this.refocusAfterEnter && this.isBrowser) {
                        setTimeout(() => {
                            if (!document.activeElement || document.activeElement === document.body) {
                                this.focusActiveItem();
                            }
                        });
                    }
                    break;
                default:
                    manager.onKeydown(event);
            }
        }
    }
    hasChildren(item) {
        return !!item.items?.length && item.items.some(child => child.title && child.title !== '-');
    }
    closePreviousChild() {
        if (this._previousChild) {
            this._previousChild.close();
            this._previousChild = undefined;
            this._changeDetector.detectChanges();
        }
    }
    openAndCloseChildren() {
        this.closePreviousChild();
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = active.childList;
            if (activeList && activeList.getHostElement() !== this.getHostElement()) {
                activeList.open();
                this._previousChild = activeList;
                this._changeDetector.detectChanges();
            }
        }
    }
    open() {
        this.keepOpen = true;
    }
    close() {
        this.keepOpen = false;
    }
    setActiveItem(activeItem, ignoreNextChange = false) {
        if (!this._keyManager.activeItem ||
            activeItem.getHostElement() !== this._keyManager.activeItem.getHostElement()) {
            this.ignoreNextChange = ignoreNextChange;
            this._keyManager.setActiveItem(activeItem);
        }
    }
    focusActiveItem(origin = 'program') {
        this._keyManager.activeItem?.focus();
    }
    /**
     * Focus the first item in the menu.
     * @param origin Action from which the focus originated. Used to set the correct styling.
     */
    focusFirstItem(origin = 'program', reverse = false, fromParent = false) {
        const itemIndexes = Array.from(this.interactableList.allItemsQueryList)
            .map((item, index) => {
            if (item.parentList &&
                !item.disabled &&
                item.parentList.getHostElement() === this.getHostElement()) {
                return index;
            }
            else {
                return -1;
            }
        })
            .filter(index => index !== -1);
        if (itemIndexes.length > 0) {
            let actualReverse = reverse;
            if (fromParent && this.focusLastFromParent) {
                actualReverse = !actualReverse;
            }
            const actualIndex = actualReverse ? itemIndexes[itemIndexes.length - 1] : itemIndexes[0];
            const activeItem = this.interactableList.allItemsQueryList.get(actualIndex);
            if (this._keyManager.activeItemIndex === actualIndex) {
                this.focusActiveItem();
                this.openAndCloseChildren();
                addEventListener('click', this.onClickOut);
            }
            else {
                this._keyManager.setFocusOrigin(origin).setActiveItem(activeItem);
            }
            const activeItemHost = activeItem.getHostElement();
            if (this.interactableList.parent?.integratedNestedItems.find(item => item.element === activeItemHost)) {
                this.parentList.setActiveItem(activeItem);
            }
            else if (this.interactableList.integratedParentItem) {
                this.parentList.setActiveItem(this.interactableList.integratedParentItem.directive);
            }
        }
    }
    hasAnyChildren() {
        return this.items.some(item => item.items?.length);
    }
    levelOfChildren(counter = -1, items = this.entries.filter(entry => entry.isVisible).map(entry => entry.item)) {
        return Math.max(0, ...items.map(subitem => {
            if (!subitem.disabled && subitem.items?.length) {
                return this.levelOfChildren(counter + 1, subitem.items);
            }
            else {
                return counter + 1;
            }
        }));
    }
    onParentScrollOrResize = () => {
        if (this.responsiveDropstart || this.responsivelyDropUpwards) {
            const visibleEntries = this.entries.filter(item => item.isVisible);
            if (visibleEntries.length) {
                setTimeout(() => {
                    this.zone.run(() => {
                        if (this.responsiveDropstart) {
                            this.responsivelyChangeDirection(visibleEntries.map(item => item.element));
                        }
                        if (this.responsivelyDropUpwards) {
                            visibleEntries.forEach(entry => {
                                this.responsivelyCheckIfUpwards(entry.element, entry.itemElement, entry.item);
                            });
                        }
                    });
                });
            }
        }
    };
    responsivelyChangeDirection(elements) {
        const menu = this.getHostElement();
        const levelOfChildren = this.levelOfChildren();
        const { responsiveDirection } = responsivelyCheckDirection({
            isScrolling: false,
            currentDirection: this.dropstart ? 'start' : 'end',
            contentElements: elements,
            hostElement: menu,
            placement: '',
            placementReferenceElement: menu,
            align: 'center',
            responsiveDirectionToPlacement: false,
            closeOnPlacementReferenceScrollOut: false,
            closeOnContentScrollOut: false,
            minSpaceThresholdFactor: levelOfChildren > 1 ? levelOfChildren - 1 : undefined
        });
        const newDropstart = responsiveDirection === 'start';
        if (this.actualDropstart !== newDropstart) {
            this.actualDropstart = newDropstart;
        }
        this._changeDetector.markForCheck();
    }
    responsivelyCheckIfUpwards(element, itemElement, item) {
        const { responsiveDirection } = responsivelyCheckDirection({
            isScrolling: false,
            currentDirection: item.dropUpwards ? 'up' : 'down',
            contentElements: [element],
            hostElement: itemElement,
            placement: 'top',
            placementReferenceElement: itemElement,
            align: 'center',
            responsiveDirectionToPlacement: false,
            closeOnPlacementReferenceScrollOut: false,
            closeOnContentScrollOut: false,
            placementReverse: 'bottom',
            rtl: false
        });
        const newDropUpwards = responsiveDirection === 'up';
        if (item.dropUpwards !== newDropUpwards) {
            item.dropUpwards = newDropUpwards;
            const recursivelyApply = (subitem) => {
                if (subitem.items?.length) {
                    subitem.items?.forEach(subsubitem => {
                        subsubitem.dropUpwards = newDropUpwards;
                        if (!subsubitem.disabled) {
                            recursivelyApply(subsubitem);
                        }
                    });
                }
            };
            recursivelyApply(item);
        }
    }
    goDownLevel(reverse = false, start = false) {
        if (this.parentList) {
            this.closePreviousChild();
            const nestedOrParentItem = this.interactableList
                ? (this.interactableList.integratedParentItem
                    ? this.interactableList.integratedParentItem
                    : this.interactableList.items.find(item => item.parentIntegrationIndex !== -1 || item.nestedIntegration))?.directive
                : undefined;
            let actualReverse = reverse;
            const parent = this.parentList;
            if (parent.orientation === 'ets') {
                actualReverse = !actualReverse;
            }
            if (start && !parent.start) {
                actualReverse = !actualReverse;
            }
            parent.focusActiveItem('program', actualReverse, nestedOrParentItem);
        }
        else {
            this.goUpLevel(true);
        }
    }
    goUpLevel(reverse = false, start = false) {
        const active = this._keyManager.activeItem;
        if (active) {
            const activeList = active.childList;
            if (activeList) {
                activeList.focusFirstItem('program', reverse, true);
            }
            else if (this.parentList) {
                this.goDownLevel(true, start);
            }
        }
    }
    onClickOut = (event) => this.clickOutHandler(event);
    clickOutHandler(event) {
        const target = event.target;
        if (this.interactableList && !this._keyManager?.activeItem?.hasChildElement(target)) {
            this.closePreviousChild();
            removeEventListener('click', this.onClickOut);
        }
    }
    focusInEvent(target) {
        if (this.connectionsCalculated) {
            if (target === this.getHostElement()) {
                if (this.focus) {
                    this.focusFirstItem('program', this.focus === 'last');
                }
            }
            else {
                if (this.interactableList) {
                    const foundItem = this.interactableList.items.find(item => item.element.contains(target));
                    if (foundItem) {
                        this.setActiveItem(foundItem.directive);
                    }
                }
                if (this.tabindex !== '-1') {
                    this.tabindex = '-1';
                }
            }
        }
    }
    focusOutEvent() {
        if (this.tabindex === '-1') {
            Promise.resolve().then(() => (this.tabindex = !this.isSubList || !this.parentList ? '0' : ''));
        }
    }
    /** Returns the host DOM element. */
    getHostElement() {
        return this._elementRef.nativeElement;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiMenuComponent, selector: "si-menu", inputs: { items: "items", dropleft: "dropleft", dropstart: "dropstart", responsiveDropleft: "responsiveDropleft", responsiveDropstart: "responsiveDropstart", responsivelyDropUpwards: "responsivelyDropUpwards", actionParam: "actionParam", focusOnLoad: "focusOnLoad", focus: "focus", focusLastFromParent: "focusLastFromParent", isSubList: "isSubList", parentItem: "parentItem", refocusAfterEnter: "refocusAfterEnter", position: "position", autofocusFromParent: "autofocusFromParent", relativePositioning: "relativePositioning", forceTitle: "forceTitle" }, outputs: { activeChange: "activeChange" }, host: { listeners: { "keydown": "handleKeydown($event)", "focusin": "focusInEvent($event.target)", "focusout": "focusOutEvent()" }, properties: { "class.dropdown-menu-relative": "this.relativePositioning", "class.show": "this.keepOpen", "attr.tabindex": "this.tabindex" }, classAttribute: "dropdown-menu" }, usesOnChanges: true, ngImport: i0, template: "<ul class=\"dropdown-menu-child\" role=\"menu\">\n  <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n    <li\n      *ngIf=\"!hasChildren(item); else childrenTemplate\"\n      [attr.role]=\"item.title === '-' ? 'separator' : 'presentation'\"\n      [class.dropdown-divider]=\"item.title === '-'\"\n    >\n      <si-menu-item\n        *ngIf=\"item.title !== '-'\"\n        badgeStyle=\"inline\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"true\"\n        [disableLinklessClick]=\"true\"\n        [forceTitle]=\"forceTitle\"\n        (activeChange)=\"linkActiveChange($event)\"\n      ></si-menu-item>\n    </li>\n    <ng-template #childrenTemplate>\n      <li\n        role=\"presentation\"\n        [class.dropstart]=\"actualDropstart\"\n        [class.dropend]=\"!actualDropstart\"\n        [class.disabled]=\"item.disabled\"\n      >\n        <si-menu-item\n          #parentItem\n          badgeStyle=\"inline\"\n          dropdownCaret=\"end\"\n          [item]=\"item\"\n          [actionParam]=\"actionParam\"\n          [disableParentClick]=\"true\"\n          [disableLinklessClick]=\"true\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu-item>\n        <si-menu\n          [class.hidden]=\"childrenHidden\"\n          [items]=\"item.items!\"\n          [dropstart]=\"actualDropstart\"\n          [responsiveDropstart]=\"responsiveDropstart\"\n          [class.dropupwards]=\"item.dropUpwards\"\n          [focusLastFromParent]=\"!!item.dropUpwards\"\n          [responsivelyDropUpwards]=\"responsivelyDropUpwards\"\n          [actionParam]=\"actionParam\"\n          [isSubList]=\"true\"\n          [parentItem]=\"parentItem\"\n          [forceTitle]=\"forceTitle\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu>\n      </li>\n    </ng-template>\n  </ng-container>\n</ul>\n", styles: [":host{outline:none}:host.dropdown-menu-relative{display:inline-block;position:relative;inset-block-start:0}:host.dropupwards{inset-block:auto 0}:host.hidden{visibility:hidden}.dropdown-menu-child .dropdown-menu{margin-inline:0}.dropdown-menu-child>li:not(.disabled):active>.dropdown-menu,.dropdown-menu-child>li:not(.disabled):hover>.dropdown-menu{display:block}.dropdown-menu-child{list-style:none;padding:0;margin:0}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SiMenuComponent, selector: "si-menu", inputs: ["items", "dropleft", "dropstart", "responsiveDropleft", "responsiveDropstart", "responsivelyDropUpwards", "actionParam", "focusOnLoad", "focus", "focusLastFromParent", "isSubList", "parentItem", "refocusAfterEnter", "position", "autofocusFromParent", "relativePositioning", "forceTitle"], outputs: ["activeChange"] }, { kind: "component", type: SiMenuItemComponent, selector: "si-menu-item", inputs: ["item", "badgeStyle", "dropdownCaret", "actionParam", "disableParentClick", "disableLinklessClick", "disableListInteraction", "focusStylingInside", "forceActive", "forceTitle", "itemClass", "dropdownToggle"], outputs: ["activeChange"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-menu', host: {
                        class: 'dropdown-menu'
                    }, template: "<ul class=\"dropdown-menu-child\" role=\"menu\">\n  <ng-container *ngFor=\"let item of items; trackBy: trackByIndex\">\n    <li\n      *ngIf=\"!hasChildren(item); else childrenTemplate\"\n      [attr.role]=\"item.title === '-' ? 'separator' : 'presentation'\"\n      [class.dropdown-divider]=\"item.title === '-'\"\n    >\n      <si-menu-item\n        *ngIf=\"item.title !== '-'\"\n        badgeStyle=\"inline\"\n        [item]=\"item\"\n        [actionParam]=\"actionParam\"\n        [disableParentClick]=\"true\"\n        [disableLinklessClick]=\"true\"\n        [forceTitle]=\"forceTitle\"\n        (activeChange)=\"linkActiveChange($event)\"\n      ></si-menu-item>\n    </li>\n    <ng-template #childrenTemplate>\n      <li\n        role=\"presentation\"\n        [class.dropstart]=\"actualDropstart\"\n        [class.dropend]=\"!actualDropstart\"\n        [class.disabled]=\"item.disabled\"\n      >\n        <si-menu-item\n          #parentItem\n          badgeStyle=\"inline\"\n          dropdownCaret=\"end\"\n          [item]=\"item\"\n          [actionParam]=\"actionParam\"\n          [disableParentClick]=\"true\"\n          [disableLinklessClick]=\"true\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu-item>\n        <si-menu\n          [class.hidden]=\"childrenHidden\"\n          [items]=\"item.items!\"\n          [dropstart]=\"actualDropstart\"\n          [responsiveDropstart]=\"responsiveDropstart\"\n          [class.dropupwards]=\"item.dropUpwards\"\n          [focusLastFromParent]=\"!!item.dropUpwards\"\n          [responsivelyDropUpwards]=\"responsivelyDropUpwards\"\n          [actionParam]=\"actionParam\"\n          [isSubList]=\"true\"\n          [parentItem]=\"parentItem\"\n          [forceTitle]=\"forceTitle\"\n          (activeChange)=\"linkActiveChange($event)\"\n        ></si-menu>\n      </li>\n    </ng-template>\n  </ng-container>\n</ul>\n", styles: [":host{outline:none}:host.dropdown-menu-relative{display:inline-block;position:relative;inset-block-start:0}:host.dropupwards{inset-block:auto 0}:host.hidden{visibility:hidden}.dropdown-menu-child .dropdown-menu{margin-inline:0}.dropdown-menu-child>li:not(.disabled):active>.dropdown-menu,.dropdown-menu-child>li:not(.disabled):hover>.dropdown-menu{display:block}.dropdown-menu-child{list-style:none;padding:0;margin:0}\n"] }]
        }], propDecorators: { items: [{
                type: Input,
                args: [{ required: true }]
            }], dropleft: [{
                type: Input
            }], dropstart: [{
                type: Input
            }], responsiveDropleft: [{
                type: Input
            }], responsiveDropstart: [{
                type: Input
            }], responsivelyDropUpwards: [{
                type: Input
            }], actionParam: [{
                type: Input
            }], focusOnLoad: [{
                type: Input
            }], focus: [{
                type: Input
            }], focusLastFromParent: [{
                type: Input
            }], isSubList: [{
                type: Input
            }], parentItem: [{
                type: Input
            }], refocusAfterEnter: [{
                type: Input
            }], position: [{
                type: Input
            }], autofocusFromParent: [{
                type: Input
            }], relativePositioning: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.dropdown-menu-relative']
            }], forceTitle: [{
                type: Input
            }], activeChange: [{
                type: Output
            }], keepOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], focusInEvent: [{
                type: HostListener,
                args: ['focusin', ['$event.target']]
            }], focusOutEvent: [{
                type: HostListener,
                args: ['focusout']
            }] } });

/**
 * Method to help you toggle the selection state of `MenuItems` depending on their
 * id value in a mutually exclusive fashion. It marks a specific item
 * as selected and unselects the others.
 * This can be used for example to mark the currently selected theme
 * while de-selecting the ones that are not active.
 *
 * @param menuItems Array of items that should be (de)-selected
 * @param id Id of the item that should get selected
 * @param type If it should display a check or a radio select
 * @returns Modified menuItems Array with one item selected
 **/
const selectMenuItemById = (menuItems, id, type = 'check') => menuItems.map(item => {
    item.selectionState = item.id === id ? type : '';
    return item;
});

class SiMenuModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiMenuModule, declarations: [SiMenuComponent, SiMenuItemComponent], imports: [CommonModule, SiLinkModule, SiTranslateModule], exports: [SiMenuComponent, SiMenuItemComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuModule, imports: [CommonModule, SiLinkModule, SiTranslateModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SiLinkModule, SiTranslateModule],
                    declarations: [SiMenuComponent, SiMenuItemComponent],
                    exports: [SiMenuComponent, SiMenuItemComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiMenuComponent, SiMenuItemComponent, SiMenuModule, selectMenuItemById };
